import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  Users,
  Pencil,
  Trash2,
  UserPlus,
  UserMinus,
  UserCheck,
  PlayCircle,
  Upload,
  Info,
} from "lucide-react";
import i18n, { t, dateLocale } from "i18n";

// Activity type configuration (icons and colors only)
const ACTIVITY_CONFIG = {
  team_created: {
    Icon: Users,
    bgColor: "#F0FDF4",
    iconColor: "#10B981",
  },
  team_edited: {
    Icon: Pencil,
    bgColor: "#EEF2FF",
    iconColor: "#6366F1",
  },
  team_deleted: {
    Icon: Trash2,
    bgColor: "#FEF2F2",
    iconColor: "#DC2626",
  },
  member_added: {
    Icon: UserPlus,
    bgColor: "#F0FDF4",
    iconColor: "#10B981",
  },
  member_removed: {
    Icon: UserMinus,
    bgColor: "#FEF2F2",
    iconColor: "#DC2626",
  },
  player_created: {
    Icon: UserPlus,
    bgColor: "#F0FDF4",
    iconColor: "#10B981",
  },
  player_added: {
    Icon: UserCheck,
    bgColor: "#F0FDF4",
    iconColor: "#10B981",
  },
  player_removed: {
    Icon: UserMinus,
    bgColor: "#FEF3E7",
    iconColor: "#E07A3A",
  },
  player_edited: {
    Icon: Pencil,
    bgColor: "#EEF2FF",
    iconColor: "#6366F1",
  },
  player_deleted: {
    Icon: Trash2,
    bgColor: "#FEF2F2",
    iconColor: "#DC2626",
  },
  session_created: {
    Icon: PlayCircle,
    bgColor: "#F0FDF4",
    iconColor: "#10B981",
  },
  session_uploaded: {
    Icon: Upload,
    bgColor: "#F0FDF4",
    iconColor: "#10B981",
  },
  session_deleted: {
    Icon: Trash2,
    bgColor: "#FEF2F2",
    iconColor: "#DC2626",
  },
};

// Default config for unknown activity types
const DEFAULT_CONFIG = {
  Icon: Info,
  bgColor: "#F3F4F6",
  iconColor: "#6B7280",
};

// Format relative timestamp
const formatRelativeTime = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return t("common.time.justNow");
  if (diffMins < 60)
    return t("workspace.activity.minutesAgo", { count: diffMins });
  if (diffHours < 24) return t("common.time.hoursAgo", { count: diffHours });
  if (diffDays === 1) return t("common.time.yesterday");
  if (diffDays < 7) return t("common.time.daysAgo", { count: diffDays });

  // Format as date for older items
  return date.toLocaleDateString(dateLocale("en-US"), {
    month: "short",
    day: "numeric",
  });
};

// Time of day, for rows under a dated heading ("Yesterday", "Monday…").
const formatClockTime = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleTimeString(dateLocale("en-GB"), {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

// "Ada, Ben and Cy" in the current language; repeated names collapse to
// "Tuesday Session ×3".
const formatNameList = (allNames) => {
  const counts = new Map();
  allNames.forEach((name) => counts.set(name, (counts.get(name) || 0) + 1));
  const names = [...counts].map(([name, n]) =>
    n > 1 ? `${name} ×${n}` : name
  );
  try {
    return new Intl.ListFormat(i18n.language, {
      style: "long",
      type: "conjunction",
    }).format(names);
  } catch {
    return names.join(", ");
  }
};

// Clickable resource link component
const ResourceLink = ({ children, onClick, isClickable }) => {
  if (!isClickable) {
    return <span className="font-semibold">{children}</span>;
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="font-semibold text-[#374151] transition duration-150 hover:text-[#E07A3A] hover:underline"
    >
      {children}
    </button>
  );
};

/**
 * @param {object} activity - the (newest) event this row shows
 * @param {Array} members - every event merged into this row (see
 *   activityGroups.js); more than one renders a counted label
 * @param {boolean} showClockTime - show the time of day instead of a
 *   relative time (for rows under a dated heading)
 */
const ActivityRow = ({
  activity,
  members = [activity],
  showClockTime = false,
  isLast = false,
}) => {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  // Subscribe to language changes so labels re-render
  useTranslation();

  const config = ACTIVITY_CONFIG[activity.activity_type] || DEFAULT_CONFIG;
  const { Icon, bgColor, iconColor } = config;
  const timestamp = showClockTime
    ? formatClockTime(activity.created_at)
    : formatRelativeTime(activity.created_at);
  const isGroup = members.length > 1;
  const groupNames = isGroup
    ? formatNameList(members.map((m) => m.resource_name).filter(Boolean))
    : "";

  // Check if resource is deleted (can't navigate to deleted resources)
  const isDeleted = activity.activity_type?.includes("deleted");

  // Navigation helpers
  const navigateToTeam = (teamId, playerId = null) => {
    if (teamId && workspaceId) {
      const url = playerId
        ? `/v2/${workspaceId}/team/${teamId}?player=${playerId}`
        : `/v2/${workspaceId}/team/${teamId}`;
      navigate(url);
    }
  };

  // Get team ID for navigation
  const getTeamIdForResource = () => {
    if (activity.resource_type === "team") {
      return activity.resource_id || activity.team_id;
    }
    return activity.team_id;
  };

  const teamId = getTeamIdForResource();
  const canNavigateToTeam = !isDeleted && !!teamId;

  // Render the activity label with clickable resource names
  const renderLabel = () => {
    const { activity_type, actor_name, resource_name, team_name } = activity;

    // Team link component
    const TeamLink = ({ children }) => (
      <ResourceLink
        isClickable={canNavigateToTeam}
        onClick={() => navigateToTeam(teamId)}
      >
        {children}
      </ResourceLink>
    );

    // Player link - navigates to the team with player selected
    const PlayerLink = ({ children }) => (
      <ResourceLink
        isClickable={canNavigateToTeam && !!activity.resource_id}
        onClick={() => navigateToTeam(activity.team_id, activity.resource_id)}
      >
        {children}
      </ResourceLink>
    );

    // Session link - navigates to the team
    const SessionLink = ({ children }) => (
      <ResourceLink
        isClickable={canNavigateToTeam}
        onClick={() => navigateToTeam(activity.team_id)}
      >
        {children}
      </ResourceLink>
    );

    const components = {
      bold: <span className="font-semibold" />,
      team: <TeamLink />,
      player: <PlayerLink />,
      session: <SessionLink />,
    };
    const values = {
      actor: actor_name ?? "",
      resource: resource_name ?? "",
      teamName: team_name ?? "",
      count: members.length,
    };

    // Label keys per activity type; "WithTeam" variants include the team link
    const LABEL_KEYS = {
      team_created: "teamCreated",
      team_edited: "teamEdited",
      team_deleted: "teamDeleted",
      member_added: "memberAdded",
      member_removed: "memberRemoved",
      player_created: "playerCreated",
      player_added: "playerAdded",
      player_removed: "playerRemoved",
      player_edited: "playerEdited",
      player_deleted: "playerDeleted",
      session_created: "sessionCreated",
      session_uploaded: "sessionUploaded",
      session_deleted: "sessionDeleted",
    };
    const TEAM_VARIANTS = [
      "player_created",
      "player_added",
      "player_removed",
      "session_created",
      "session_uploaded",
    ];

    const labelKey = LABEL_KEYS[activity_type];
    if (!labelKey) {
      return (
        activity.activity_type_display ||
        activity.activity_type ||
        t("workspace.activity.fallback")
      );
    }

    const suffix =
      team_name && TEAM_VARIANTS.includes(activity_type) ? "WithTeam" : "";
    const grouped = isGroup ? "Grouped" : "";

    return (
      <Trans
        i18nKey={`workspace.activity.labels.${labelKey}${grouped}${suffix}`}
        count={isGroup ? members.length : undefined}
        values={values}
        components={components}
      />
    );
  };

  return (
    <div
      className={`flex items-center justify-between px-4 py-2.5 ${
        !isLast ? "border-b border-[#F3F4F6]" : ""
      }`}
    >
      {/* Left side */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: bgColor }}
        >
          <Icon size={14} style={{ color: iconColor }} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs text-[#374151]">{renderLabel()}</p>
          {groupNames && (
            <p
              className="mt-0.5 truncate text-[11px] text-[#9CA3AF]"
              title={groupNames}
            >
              {groupNames}
            </p>
          )}
        </div>
      </div>

      {/* Right side - timestamp */}
      <div className="ml-3 flex flex-shrink-0 items-center gap-2">
        <span className="whitespace-nowrap text-[11px] text-[#9CA3AF]">
          {timestamp}
        </span>
      </div>
    </div>
  );
};

export default ActivityRow;
